import React, { useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";

const CONSENT_KEY = "rarium_cookie_consent_v1";

function readConsent() {
  try {
    const saved = window.localStorage.getItem(CONSENT_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const { t } = useLanguage();
  const [consent, setConsent] = useState(readConsent);
  const [editing, setEditing] = useState(false);
  const [analytics, setAnalytics] = useState(() => readConsent()?.analytics ?? false);
  const [marketing, setMarketing] = useState(() => readConsent()?.marketing ?? false);
  const isVisible = consent === null || editing;

  useEffect(() => {
    const openSettings = () => setEditing(true);
    window.addEventListener("rarium:open-cookie-settings", openSettings);
    return () => window.removeEventListener("rarium:open-cookie-settings", openSettings);
  }, []);

  const saveConsent = (choices) => {
    const nextConsent = {
      necessary: true,
      analytics: choices.analytics,
      marketing: choices.marketing,
      savedAt: new Date().toISOString(),
    };

    try {
      window.localStorage.setItem(CONSENT_KEY, JSON.stringify(nextConsent));
    } catch {
      // Keep the choice active for this visit if browser storage is unavailable.
    }

    setConsent(nextConsent);
    setAnalytics(nextConsent.analytics);
    setMarketing(nextConsent.marketing);
    setEditing(false);
  };

  if (!isVisible) return null;

  return (
    <section
      aria-label={t("Preferenze cookie")}
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-stone-300 bg-[#FAFAF9] shadow-[0_-8px_32px_rgba(28,25,23,0.12)]"
      data-testid="cookie-consent-banner"
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-5 px-5 py-5 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10 lg:px-10">
        <div className="max-w-2xl">
          <p className="font-serif text-2xl text-stone-900">{t("La tua privacy, le tue scelte")}</p>
          <p className="mt-1 text-sm leading-relaxed text-stone-600">
            {t("Usiamo cookie tecnici necessari al funzionamento del sito. Con il tuo consenso possiamo attivare cookie di analisi e marketing; puoi modificarlo in qualsiasi momento.")}
          </p>
          {editing && (
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3 text-sm text-stone-800">
              <label className="flex min-h-12 items-center gap-2">
                <input type="checkbox" checked disabled aria-label="Cookie necessari sempre attivi" />
                <span>{t("Necessari, sempre attivi")}</span>
              </label>
              <label className="flex min-h-12 items-center gap-2">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(event) => setAnalytics(event.target.checked)}
                  data-testid="cookie-analytics-toggle"
                />
                <span>{t("Analisi")}</span>
              </label>
              <label className="flex min-h-12 items-center gap-2">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(event) => setMarketing(event.target.checked)}
                  data-testid="cookie-marketing-toggle"
                />
                <span>{t("Marketing")}</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:justify-end">
          {editing && (
            <button
              type="button"
              onClick={() => saveConsent({ analytics: false, marketing: false })}
              className="min-h-12 border border-stone-400 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-stone-800 transition-colors hover:bg-stone-100"
              data-testid="cookie-necessary-only-btn"
            >
              {t("Solo necessari")}
            </button>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => saveConsent({ analytics: false, marketing: false })}
              className="min-h-12 border border-stone-400 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-stone-800 transition-colors hover:bg-stone-100"
              data-testid="cookie-reject-optional-btn"
            >
              {t("Rifiuta opzionali")}
            </button>
          )}
          {editing ? (
            <button
              type="button"
              onClick={() => saveConsent({ analytics, marketing })}
              className="min-h-12 bg-[#8C3A3A] px-5 py-3 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-[#7F3434]"
              data-testid="cookie-save-preferences-btn"
            >
              {t("Salva preferenze")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="min-h-12 border border-stone-400 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-stone-800 transition-colors hover:bg-stone-100"
              data-testid="cookie-customize-btn"
            >
              {t("Personalizza")}
            </button>
          )}
          <button
            type="button"
            onClick={() => saveConsent({ analytics: true, marketing: true })}
            className="min-h-12 bg-[#8C3A3A] px-5 py-3 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-[#7F3434]"
            data-testid="cookie-accept-all-btn"
          >
            {t("Accetta tutti")}
          </button>
        </div>
      </div>
    </section>
  );
}